import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import TagInput from "../components/TagInput";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

const SENIORITY = ["junior", "confirmé", "senior", "expert"];
const REMOTE = ["onsite", "hybrid", "remote"];
const LANGS = ["fr", "en", "es", "de", "it"];
const INDUSTRIES = [
  "IT services",
  "Finance",
  "Retail",
  "Manufacturing",
  "Healthcare",
  "Public sector",
  "Media & entertainment",
  "Energy",
  "Telecoms",
  "Other",
];

const EMPTY = {
  title: "",
  description: "",
  required_skills: [],
  seniority: "senior",
  tjm_type: "range",
  tjm_min: 500,
  tjm_max: 700,
  start_date: new Date(Date.now() + 30 * 86400_000).toISOString().slice(0, 10),
  duration_months: 6,
  extension_likely: false,
  location_city: "Paris",
  country: "FR",
  remote_policy: "hybrid",
  end_client_industry: "",
  mask_end_client: false,
  languages_required: ["fr"],
  visibility: "open",
  invited_agency_org_ids: [],
};

export default function MissionComposer({ mode }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = mode === "edit";
  const { user } = useAuth();

  const [form, setForm] = useState(EMPTY);
  const [selectedAgencies, setSelectedAgencies] = useState([]); // list of {id,name,logo_url,specialties}
  const [savedId, setSavedId] = useState(id || null);
  const [savedStatus, setSavedStatus] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [agencyQuery, setAgencyQuery] = useState("");
  const [agencyResults, setAgencyResults] = useState([]);
  const [recommended, setRecommended] = useState([]);

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e,
    }));

  const loadInvited = useCallback(async (missionId) => {
    if (!missionId) return;
    try {
      const { data } = await api.get(`/missions/${missionId}/invited-agencies`);
      setSelectedAgencies(data);
    } catch (err) {
      // non-fatal: no invited agencies for this mission yet
      console.warn("loadInvited failed:", err);
    }
  }, []);

  const loadRecommended = useCallback(async (missionId) => {
    if (!missionId) {
      setRecommended([]);
      return;
    }
    try {
      const { data } = await api.get(`/missions/${missionId}/recommended-agencies`, {
        params: { limit: 8 },
      });
      setRecommended(data);
    } catch (err) {
      console.warn("loadRecommended failed:", err);
      setRecommended([]);
    }
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/missions/${id}`);
        setForm({
          title: data.title,
          description: data.description,
          required_skills: data.required_skills || [],
          seniority: data.seniority,
          tjm_type: data.tjm_type,
          tjm_min: data.tjm_min,
          tjm_max: data.tjm_max,
          start_date: data.start_date,
          duration_months: data.duration_months,
          extension_likely: !!data.extension_likely,
          location_city: data.location_city,
          country: data.country || "FR",
          remote_policy: data.remote_policy,
          end_client_industry: data.end_client_industry || "",
          mask_end_client: !!data.mask_end_client,
          languages_required: data.languages_required || [],
          visibility: data.visibility,
          invited_agency_org_ids: data.invited_agency_org_ids || [],
        });
        setSavedStatus(data.status);
        setSavedId(data.id);
        await loadInvited(data.id);
        await loadRecommended(data.id);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
        navigate("/missions/mine");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate, t, loadInvited, loadRecommended]);

  useEffect(() => {
    if (form.visibility !== "invite_only") return;
    let cancelled = false;
    (async () => {
      if (!agencyQuery) {
        setAgencyResults([]);
        return;
      }
      try {
        const { data } = await api.get("/agencies/search", {
          params: { q: agencyQuery, limit: 8 },
        });
        if (!cancelled) setAgencyResults(data);
      } catch (err) {
        console.warn("agencies/search failed:", err);
        if (!cancelled) setAgencyResults([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agencyQuery, form.visibility]);

  const toggleLang = (l) =>
    setForm((f) => ({
      ...f,
      languages_required: f.languages_required.includes(l)
        ? f.languages_required.filter((x) => x !== l)
        : [...f.languages_required, l],
    }));

  const toggleAgency = (agency) => {
    setSelectedAgencies((sel) =>
      sel.some((a) => a.id === agency.id)
        ? sel.filter((a) => a.id !== agency.id)
        : [...sel, agency]
    );
  };

  const validFixed = form.tjm_type !== "fixed" || form.tjm_min === form.tjm_max;
  const validRange = form.tjm_min <= form.tjm_max;
  const canSubmit =
    !!form.title.trim() &&
    !!form.description.trim() &&
    !!form.location_city.trim() &&
    validFixed &&
    validRange;

  const buildPayload = () => {
    const inviteIds = form.visibility === "invite_only" ? selectedAgencies.map((a) => a.id) : [];
    return {
      ...form,
      tjm_min: Number(form.tjm_min),
      tjm_max: Number(form.tjm_type === "fixed" ? form.tjm_min : form.tjm_max),
      duration_months: Number(form.duration_months),
      end_client_industry: form.end_client_industry || null,
      invited_agency_org_ids: inviteIds,
    };
  };

  const save = useCallback(
    async (targetStatus) => {
      if (!canSubmit) {
        toast.error(t("mission.error_missing_required"));
        return null;
      }
      setSaving(true);
      try {
        const payload = { ...buildPayload(), status: targetStatus || "draft" };
        if (savedId) {
          delete payload.status;
          await api.patch(`/missions/${savedId}`, payload);
          if (targetStatus && targetStatus !== savedStatus) {
            await api.post(`/missions/${savedId}/status`, { new_status: targetStatus });
          }
          toast.success(t("mission.saved_toast"));
          return savedId;
        } else {
          const { data } = await api.post("/missions", payload);
          setSavedId(data.id);
          setSavedStatus(data.status);
          toast.success(
            targetStatus === "open" ? t("mission.published_toast") : t("mission.draft_saved_toast")
          );
          return data.id;
        }
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
        return null;
      } finally {
        setSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canSubmit, form, savedId, savedStatus, selectedAgencies, t]
  );

  const onSaveDraft = async () => {
    const newId = await save("draft");
    if (newId) navigate("/missions/mine");
  };
  const onPublish = async () => {
    const newId = await save("open");
    if (newId) navigate(`/missions/${newId}`);
  };

  const invitedIds = useMemo(() => new Set(selectedAgencies.map((a) => a.id)), [selectedAgencies]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="p-10 text-center text-slate-500">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="mission-composer-page">
      <Toaster richColors position="top-right" />
      <Header />
      <main className="mx-auto max-w-4xl px-6 py-10">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">
              {isEdit ? t("mission.edit_eyebrow") : t("mission.create_eyebrow")}
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              {isEdit ? t("mission.edit_title") : t("mission.create_title")}
            </h1>
          </div>
          <Link
            to="/missions/mine"
            className="text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            ← {t("common.back")}
          </Link>
        </div>

        <form className="space-y-6" onSubmit={(e) => e.preventDefault()} data-testid="mission-composer-form">
          {/* Basics */}
          <Section title={t("mission.section_basics")}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label={t("mission.title_label")} className="md:col-span-2">
                <input
                  value={form.title}
                  onChange={set("title")}
                  className={inputCls}
                  data-testid="mc-title"
                  required
                />
              </Field>
              <Field label={t("mission.description_label")} className="md:col-span-2">
                <textarea
                  value={form.description}
                  onChange={set("description")}
                  rows={5}
                  className={inputCls}
                  data-testid="mc-description"
                  placeholder={t("mission.description_placeholder")}
                />
              </Field>
              <Field label={t("mission.required_skills")} className="md:col-span-2">
                <TagInput
                  value={form.required_skills}
                  onChange={(v) => setForm((f) => ({ ...f, required_skills: v }))}
                  placeholder={t("roster.skills_placeholder")}
                  testId="mc-skills"
                  containerTestId="mc-skills-container"
                />
              </Field>
              <Field label={t("mission.seniority_label")}>
                <select value={form.seniority} onChange={set("seniority")} className={inputCls} data-testid="mc-seniority">
                  {SENIORITY.map((s) => (
                    <option key={s} value={s}>{t(`roster.seniority_${s}`)}</option>
                  ))}
                </select>
              </Field>
              <Field label={t("mission.languages_required")}>
                <div className="flex flex-wrap gap-1.5" data-testid="mc-languages">
                  {LANGS.map((l) => {
                    const active = form.languages_required.includes(l);
                    return (
                      <button
                        key={l}
                        type="button"
                        onClick={() => toggleLang(l)}
                        data-testid={`mc-lang-${l}`}
                        className={
                          "rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider transition-colors " +
                          (active
                            ? "bg-indigo-600 text-white shadow-sm"
                            : "bg-white text-slate-700 ring-1 ring-slate-300 hover:bg-slate-100")
                        }
                      >
                        {l}
                      </button>
                    );
                  })}
                </div>
              </Field>
            </div>
          </Section>

          {/* Commercial */}
          <Section title={t("mission.section_commercial")}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label={t("mission.tjm_type")}>
                <div className="flex gap-2" data-testid="mc-tjm-type">
                  {["fixed", "range"].map((t2) => (
                    <button
                      key={t2}
                      type="button"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          tjm_type: t2,
                          tjm_max: t2 === "fixed" ? f.tjm_min : f.tjm_max,
                        }))
                      }
                      data-testid={`mc-tjm-type-${t2}`}
                      className={
                        "flex-1 rounded-lg px-3 py-2 text-sm font-semibold " +
                        (form.tjm_type === t2
                          ? "bg-indigo-600 text-white"
                          : "bg-white ring-1 ring-slate-300 text-slate-700 hover:bg-slate-50")
                      }
                    >
                      {t(`mission.tjm_${t2}`)}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label={t("mission.tjm_hint_label")}>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  {t("mission.tjm_hint_body")}
                </div>
              </Field>
              <Field label={form.tjm_type === "fixed" ? t("mission.tjm_value") : t("mission.tjm_min")}>
                <input
                  type="number"
                  min={0}
                  max={10000}
                  value={form.tjm_min}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((f) => ({
                      ...f,
                      tjm_min: v,
                      tjm_max: f.tjm_type === "fixed" ? v : f.tjm_max,
                    }));
                  }}
                  className={inputCls}
                  data-testid="mc-tjm-min"
                />
              </Field>
              {form.tjm_type === "range" && (
                <Field label={t("mission.tjm_max")}>
                  <input
                    type="number"
                    min={0}
                    max={10000}
                    value={form.tjm_max}
                    onChange={set("tjm_max")}
                    className={inputCls}
                    data-testid="mc-tjm-max"
                  />
                </Field>
              )}
              {form.tjm_type === "range" && Number(form.tjm_min) > Number(form.tjm_max) && (
                <p className="md:col-span-2 text-xs text-rose-700">
                  {t("mission.tjm_error_range")}
                </p>
              )}
            </div>
          </Section>

          {/* Timing */}
          <Section title={t("mission.section_timing")}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Field label={t("mission.start_date")}>
                <input type="date" value={form.start_date} onChange={set("start_date")} className={inputCls} data-testid="mc-start" />
              </Field>
              <Field label={t("mission.duration_months")}>
                <input type="number" min={1} max={60} value={form.duration_months} onChange={set("duration_months")} className={inputCls} data-testid="mc-duration" />
              </Field>
              <Field label={t("mission.extension_likely")}>
                <label className="mt-2 flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={form.extension_likely} onChange={set("extension_likely")} className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" data-testid="mc-extension" />
                  {t("mission.extension_body")}
                </label>
              </Field>
            </div>
          </Section>

          {/* Location */}
          <Section title={t("mission.section_location")}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Field label={t("mission.city")}>
                <input value={form.location_city} onChange={set("location_city")} className={inputCls} data-testid="mc-city" required />
              </Field>
              <Field label={t("mission.country")}>
                <input value={form.country} onChange={set("country")} className={inputCls} data-testid="mc-country" />
              </Field>
              <Field label={t("mission.remote")}>
                <div className="flex gap-1" data-testid="mc-remote">
                  {REMOTE.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, remote_policy: r }))}
                      data-testid={`mc-remote-${r}`}
                      className={
                        "flex-1 rounded-lg px-2 py-2 text-xs font-semibold " +
                        (form.remote_policy === r
                          ? "bg-indigo-600 text-white"
                          : "bg-white ring-1 ring-slate-300 text-slate-700 hover:bg-slate-50")
                      }
                    >
                      {t(`roster.remote_${r}`)}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
          </Section>

          {/* End client */}
          <Section title={t("mission.section_end_client")}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label={t("mission.end_client_industry")}>
                <select value={form.end_client_industry} onChange={set("end_client_industry")} className={inputCls} data-testid="mc-industry">
                  <option value="">{t("settings.select_placeholder")}</option>
                  {INDUSTRIES.map((i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </Field>
              <Field label={t("mission.mask_end_client_label")}>
                <label className="mt-2 flex items-start gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={form.mask_end_client} onChange={set("mask_end_client")} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" data-testid="mc-mask-end-client" />
                  <span className="text-slate-600">{t("mission.mask_end_client_help")}</span>
                </label>
              </Field>
            </div>
          </Section>

          {/* Visibility */}
          <Section title={t("mission.section_visibility")}>
            <div className="grid grid-cols-1 gap-4">
              <Field label={t("mission.visibility_label")}>
                <div className="flex gap-2" data-testid="mc-visibility">
                  {["open", "invite_only"].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, visibility: v }))}
                      data-testid={`mc-visibility-${v}`}
                      className={
                        "flex-1 rounded-lg px-3 py-2 text-sm font-semibold " +
                        (form.visibility === v
                          ? "bg-indigo-600 text-white"
                          : "bg-white ring-1 ring-slate-300 text-slate-700 hover:bg-slate-50")
                      }
                    >
                      {t(`mission.visibility_${v}`)}
                    </button>
                  ))}
                </div>
              </Field>

              {form.visibility === "invite_only" && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <h4 className="text-sm font-semibold text-slate-900">{t("mission.invite_title")}</h4>
                  <p className="mt-1 text-xs text-slate-600">{t("mission.invite_help")}</p>

                  {/* Selected chips */}
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="mc-invited">
                    {selectedAgencies.length === 0 && (
                      <span className="text-xs text-slate-500">{t("mission.invite_none")}</span>
                    )}
                    {selectedAgencies.map((a) => (
                      <span key={a.id} className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700" data-testid={`mc-invited-${a.id}`}>
                        {a.name}
                        <button type="button" onClick={() => toggleAgency(a)} className="text-indigo-500 hover:text-indigo-700">×</button>
                      </span>
                    ))}
                  </div>

                  {/* Recommended */}
                  {savedId && recommended.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        {t("mission.invite_recommended")}
                      </p>
                      <ul className="mt-2 space-y-1.5" data-testid="mc-recommended">
                        {recommended.map((a) => (
                          <AgencyPickRow
                            key={a.id}
                            agency={a}
                            selected={invitedIds.has(a.id)}
                            onToggle={() => toggleAgency(a)}
                          />
                        ))}
                      </ul>
                    </div>
                  )}
                  {!savedId && (
                    <p className="mt-3 text-xs text-slate-500">
                      {t("mission.invite_save_first")}
                    </p>
                  )}

                  {/* Search */}
                  <div className="mt-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      {t("mission.invite_search_title")}
                    </p>
                    <input
                      value={agencyQuery}
                      onChange={(e) => setAgencyQuery(e.target.value)}
                      placeholder={t("mission.invite_search_placeholder")}
                      className={`${inputCls} mt-2`}
                      data-testid="mc-invite-search"
                    />
                    {agencyResults.length > 0 && (
                      <ul className="mt-2 space-y-1.5" data-testid="mc-invite-results">
                        {agencyResults.map((a) => (
                          <AgencyPickRow
                            key={a.id}
                            agency={a}
                            selected={invitedIds.has(a.id)}
                            onToggle={() => toggleAgency(a)}
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Section>

          <div className="flex flex-wrap justify-end gap-3">
            <Link to="/missions/mine" className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              {t("common.cancel")}
            </Link>
            <button
              type="button"
              onClick={onSaveDraft}
              disabled={saving || !canSubmit}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              data-testid="mc-save-draft"
            >
              {saving ? t("common.loading") : t("mission.save_draft")}
            </button>
            <button
              type="button"
              onClick={onPublish}
              disabled={saving || !canSubmit}
              className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-60"
              data-testid="mc-publish"
            >
              {saving ? t("common.loading") : t("mission.publish")}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20";

function Section({ title, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Field({ label, className = "", children }) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      {children}
    </div>
  );
}

function AgencyPickRow({ agency, selected, onToggle }) {
  return (
    <li
      onClick={onToggle}
      data-testid={`mc-agency-row-${agency.id}`}
      className={
        "flex cursor-pointer items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors " +
        (selected
          ? "border-indigo-300 bg-indigo-50"
          : "border-slate-200 bg-white hover:bg-slate-50")
      }
    >
      <div>
        <div className="font-medium text-slate-900">{agency.name}</div>
        {agency.specialties?.length > 0 && (
          <div className="mt-0.5 text-xs text-slate-500">{agency.specialties.slice(0, 4).join(" · ")}</div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {typeof agency.overlap === "number" && (
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-100">
            +{agency.overlap}
          </span>
        )}
        <span
          className={
            "inline-flex h-5 w-5 items-center justify-center rounded-full text-xs " +
            (selected ? "bg-indigo-600 text-white" : "bg-white ring-1 ring-slate-300 text-slate-400")
          }
        >
          {selected ? "✓" : "+"}
        </span>
      </div>
    </li>
  );
}
